import {
	DBType,
	DEBUG,
	IEntityRest,
	IErrorRestResponse,
	IRestCollectionParams,
	IRestDeleteParams,
	IRestParams,
	IRestResult,
	IVIPDocument,
	IVIPDocumentOptions,
	RestAPI,
	staticImplements,
	toaster,
	VIPDocument,
	VIPDocumentFields,
} from "@/loader";
import axios, { AxiosError } from "axios";
import dayjs from "dayjs";

export interface IVIPDocumentFile {
	content: string;
}

export interface IVIPDocumentRestGetParams extends IRestParams {
	options?: IVIPDocumentOptions;
}

export interface IVIPDocumentRestCollectionParams extends IRestCollectionParams, IVIPDocumentRestGetParams  {
	orderby?: VIPDocumentFields | VIPDocumentFields[];
	id?: number;
	category?: number;
	size?: number;
	date_creation?: string;
	date_expiration?: string;
	name?: string;
	mime_type?: string;
}

@staticImplements<IEntityRest<IVIPDocument, VIPDocument>>()
export abstract class VIPDocumentRest {
	public static readonly endpoint: string = "documents";

	public static getURL(data?: IVIPDocument | VIPDocument, singular: boolean = false): string {
		let url: string[] = [RestAPI.url.replace(/\/$/, ""), RestAPI.namespace];
		url.push(VIPDocumentRest.endpoint);
		if (singular && data?.id) {
			url.push(data.id.toString());
		}
		return url.join("/");
	}

	public static getFileURL(id?: number): string {
		if (!id || Number.isNaN(id)) {
			throw new Error("The document must have a valid ID");
		}
		let url: string[] = [RestAPI.url.replace(/\/$/, ""), RestAPI.namespace, VIPDocumentRest.endpoint, id.toString(), "file"];
		return url.join("/");
	}

	public static async list(params?: IVIPDocumentRestCollectionParams): Promise<IRestResult<VIPDocument>> {
		return await RestAPI.list<IVIPDocument, VIPDocument>(VIPDocumentRest.getURL(), DBType.documents, params);
	}

	public static async get(data: IVIPDocument | VIPDocument, params?: IVIPDocumentRestGetParams): Promise<IRestResult<VIPDocument>> {
		return await RestAPI.get<IVIPDocument, VIPDocument>(VIPDocumentRest.getURL(data, true), DBType.documents, params);
	}

	public static async create(data: IVIPDocument | VIPDocument): Promise<IRestResult<VIPDocument>> {
		return await RestAPI.post<IVIPDocument, VIPDocument>(VIPDocumentRest.getURL(data), data, DBType.documents);
	}

	public static async update(data: IVIPDocument | VIPDocument): Promise<IRestResult<VIPDocument>> {
		return await RestAPI.post<IVIPDocument, VIPDocument>(VIPDocumentRest.getURL(data, true), data, DBType.documents);
	}

	public static async delete(data: IVIPDocument | VIPDocument, params?: IRestDeleteParams): Promise<IRestResult<VIPDocument>> {
		return await RestAPI.delete<IVIPDocument, VIPDocument>(VIPDocumentRest.getURL(data, true), DBType.documents, params);
	}

	public static async getFile(document: IVIPDocument): Promise<IRestResult<IVIPDocumentFile>> {
		return await axios.get<IVIPDocumentFile>(VIPDocumentRest.getFileURL(document.id)).then(response => {
			return { success: true, item: response.data };
		}).catch((error: AxiosError<IErrorRestResponse>) => {
			if (DEBUG) {
				console.log(error.response?.data);
			}
			if (error.response?.data.code !== "file_not_found") {
				toaster.toast({ message: error.response?.data.message || "", variant: "danger" });
			}
			return { success: false, error: error.response?.data };
		});
	}

	public static async uploadFile(document: VIPDocument, file: File): Promise<IRestResult<VIPDocument>> {
		let formData = new FormData();
		formData.append("file", file);

		return await axios.post<IVIPDocument>(VIPDocumentRest.getFileURL(document.id), formData, { headers: { "Content-Type": "multipart/form-data" } }).then(response => {
			document.update(response.data);
			return { success: true, item: document };
		}).catch((error: AxiosError<IErrorRestResponse>) => {
			if (DEBUG) {
				console.log(error.response?.data);
			}
			toaster.toast({ message: error.response?.data.message || "", variant: "danger" });
			return { success: false, error: error.response?.data };
		});
	}
}
